import { Lot, Reserve, StatisticsGroupBy } from "../common/types/Types";

const getObjectProperty = (obj, path) => {
  const props = path.split(".");
  let currentObj = obj;

  for (let prop of props) {
    if (currentObj?.hasOwnProperty(prop)) {
      currentObj = currentObj[prop];
    } else {
      return undefined;
    }
  }

  return currentObj;
};

const transpose = (matrix) => {
  if (!matrix?.length) {
    return [];
  }
  const [row] = matrix;

  return row.map((value, column) => matrix.map((row) => row[column]));
};

const isReserve = (
  status: "solved" | "reactivated" | "active"
): ((Reserve) => boolean) => {
  if (status === "solved") {
    return (reserve: Reserve) => {
      return reserve.statutRefStatut === 2;
    };
  } else if (status === "reactivated") {
    return (reserve: Reserve) => {
      return !!reserve.refReserveParente;
    };
  } else if (status === "active") {
    return (reserve: Reserve) => {
      return reserve.statutRefStatut === 1;
    };
  }
};

export const getFormattedDataTable = (
  reserves: Reserve[],
  lots: Lot[],
  groupBy: StatisticsGroupBy
) => {
  const groupedReserves = reserves.reduce((groupedReserves, currentReserve) => {
    let labelValue;

    switch (groupBy) {
      case "enterprise":
        labelValue = getObjectProperty(currentReserve, "entreprise.nom");
        break;
      case "phase":
        labelValue = getObjectProperty(currentReserve, "phase.libPhase");
        break;
      case "trade":
        labelValue = getObjectProperty(
          currentReserve,
          "corpsEtat.libCorpsEtat"
        );
        break;
    }

    if (!labelValue) {
      labelValue = "(Non affectés)";
    }

    if (groupedReserves[labelValue]) {
      groupedReserves[labelValue].push(currentReserve);
    } else {
      groupedReserves[labelValue] = [currentReserve];
    }

    return groupedReserves;
  }, {} as { [key: string]: Reserve[] });

  let rows = Object.keys(groupedReserves).map((lineLabel) => {
    const allReserves = groupedReserves[lineLabel];

    const lotReserves = allReserves.filter(
      (r) => !r.lot?.indicLotPartieCommune
    );
    const commonReserves = allReserves.filter(
      (r) => r.lot?.indicLotPartieCommune
    );

    const validatedLots =
      (groupBy === "phase"
        ? lots?.filter((l) => {
            return l.lotVisites.some((lv) => {
              const phaseId = [3, 4].includes(allReserves[0]?.phaseRefPhase)
                ? 5
                : allReserves[0]?.phaseRefPhase;

              return lv.phaseRefPhase === phaseId && lv.indicValidite;
            });
          })?.length
        : lots?.length) || 0;

    const lotReservesSolved = lotReserves.filter(isReserve("solved"));
    const lotReservesActive = lotReserves.filter(isReserve("active"));
    const lotReservesReActivated = lotReserves.filter(isReserve("reactivated"));

    const commonReservesReActivated = commonReserves.filter(
      isReserve("reactivated")
    );
    const commonReservesActive = commonReserves.filter(isReserve("active"));

    const allReservesReActivated = allReserves.filter(isReserve("reactivated"));
    const allReservesActive = allReserves.filter(isReserve("active"));

    return [
      lineLabel,
      lotReserves.length,
      lotReserves.length
        ? (validatedLots ? lotReserves.length / validatedLots : 0).toFixed(2)
        : "0",
      lotReservesSolved.length,
      lotReservesReActivated.length,
      lotReservesActive.length,
      `${((lotReservesActive.length / lotReserves.length) * 100 || 0).toFixed(
        0
      )}%`,
      commonReserves.length,
      commonReservesReActivated.length,
      commonReservesActive.length,
      `${(
        (commonReservesActive.length / commonReserves.length) * 100 || 0
      ).toFixed(0)}%`,
      allReserves.length,
      allReservesReActivated.length,
      allReservesActive.length,
      `${((allReservesActive.length / allReserves.length) * 100 || 0).toFixed(
        0
      )}%`,
    ];
  });

  // add all phase to the rows
  if (groupBy === "phase") {
    const PHASES_ORDER = [
      "Visite Cloison",
      "OPR",
      "Pré-livraison",
      "Livraison",
      "Réserve à 1 mois",
      "Garantie de parfait achèvement",
      "Garantie de bon fonctionnement",
      "Décennale",
    ];

    // have same order as phases order
    const orderedRows = PHASES_ORDER.map((phase) => {
      const found = rows.find((value) => value[0] === phase);

      return found ? found : [phase, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    });
    rows = orderedRows;
  }

  const totalRow = transpose(rows).map((column, index) => {
    if (index === 0) {
      return "Total";
    } else if ([2, 6, 10, 14].includes(index)) {
      return "";
    } else {
      return column.reduce((acum, value) => acum + value, 0);
    }
  });

  let firstColumnName;
  switch (groupBy) {
    case "enterprise":
      firstColumnName = "Entreprise";
      break;
    case "phase":
      firstColumnName = "Phase";
      break;
    case "trade":
      firstColumnName = "Corps d'État";
      break;
  }

  return {
    rows,
    totalRow,
    titleRow: [
      firstColumnName,
      "Nb Global",
      "Nb Moyen",
      "Nb Levée",
      "Nb Réactivée",
      "Nb Restant",
      "% Restant",
      "Nb Global",
      "Nb Réactivée",
      "Nb Restant",
      "Nb Restant",
      "Nb Global",
      "Nb Réactivée",
      "Nb Restant",
      "Nb Restant",
    ],
  };
};
